import { createContext, useContext, type ReactNode } from "react";
import type { AuthUseCase, PasswordResetUseCase } from "@app/auth";
import type { CompaniesUseCase } from "@app/company/CompaniesUseCase";
import type { SubmitJobsUseCase } from "@app/jobs/SubmitJobsUseCase";
import type { MissionsUseCase } from "@app/jobs/MissionsUseCase";
import type { JobsListUseCase, JobsDetailUseCase, MarkJobsReadyUseCase } from "@app/jobs";

/**
 * Aggregate handle for every wired-up use case in the running app. GUI's
 * composition root (`4-UI/GUI/infrastructure/composition/*`) builds these
 * and provides them via `UseCasesProvider`; components consume individual
 * use cases through the per-use-case hooks below.
 *
 * Tests wrap their render in a `<UseCasesProvider value={...}>` (or a test
 * helper) with port-mocked instances.
 */
export interface UseCases {
	auth: AuthUseCase;
	passwordReset: PasswordResetUseCase;
	companies: CompaniesUseCase;
	submitJobs: SubmitJobsUseCase;
	missions: MissionsUseCase;
	jobsList: JobsListUseCase;
	jobsDetail: JobsDetailUseCase;
	markJobsReady: MarkJobsReadyUseCase;
}

const UseCasesContext = createContext<UseCases | null>(null);

export function UseCasesProvider({ value, children }: { value: UseCases; children: ReactNode }) {
	return <UseCasesContext.Provider value={value}>{children}</UseCasesContext.Provider>;
}

function useUseCases(): UseCases {
	const ctx = useContext(UseCasesContext);
	if (!ctx) {
		throw new Error("useUseCases must be called inside <UseCasesProvider>");
	}
	return ctx;
}

export function useAuthUseCase(): AuthUseCase {
	return useUseCases().auth;
}

export function usePasswordResetUseCase(): PasswordResetUseCase {
	return useUseCases().passwordReset;
}

export function useCompaniesUseCase(): CompaniesUseCase {
	return useUseCases().companies;
}

export function useSubmitJobsUseCase(): SubmitJobsUseCase {
	return useUseCases().submitJobs;
}

export function useMissionsUseCase(): MissionsUseCase {
	return useUseCases().missions;
}

export function useJobsListUseCase(): JobsListUseCase {
	return useUseCases().jobsList;
}

export function useJobsDetailUseCase(): JobsDetailUseCase {
	return useUseCases().jobsDetail;
}

export function useMarkJobsReadyUseCase(): MarkJobsReadyUseCase {
	return useUseCases().markJobsReady;
}
